import { Button, Col, Row, Space, Table, TableColumnsType, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import {
  ApproverEntry,
  DocumentLinkEntry,
  ReviewerEntry,
  SorItem,
  WorkOrderFormValues,
  approachLabel,
  REVIEWER_ORDER,
  approvalAuthorities,
  approverOrder,
  displayDate as asDate,
  hrefOf,
} from "../../Constants";
import { TemplateOption } from "./ProjectInformation";
import { sorLineColumns } from "./SOR";
import { PDFViewer } from "../../../../Components/PDFViewer/PDFViewer";
import { WrapperCard } from "../../../../Components/WrapperCard/WrapperCard";
import vars from "@styles/variables.module.scss";
import styles from "../../WorkOrder.module.scss";

interface Props {
  values: WorkOrderFormValues;
  templates: TemplateOption[];
  items: SorItem[];
  files: File[];
  savedDocuments?: any[];
  links: DocumentLinkEntry[];
  reviewers: ReviewerEntry[];
  approvers: ApproverEntry[];
  authority: string;
  onBack: () => void;
  onSaveDraft: () => void;
  isSavingDraft?: boolean;
  onSubmit: () => void;
  onEditStep: (step: number) => void;
  isSubmitting?: boolean;
}

/**
 * One label/value pair, drawn the way the SPA (ePOD) Review & Submit draws
 * them — the label as a navy heading over its value — so the two review pages
 * read alike. The work order detail page uses it too.
 */
export const Field = ({ label, value, full }: { label: string; value?: React.ReactNode; full?: boolean }) => (
  <Col
    xs={24}
    md={full ? 24 : 8}
  >
    {/* SPA's own spacing (getItemText): default gap, the heading's own
        margin, whitespace4 below — the tighter values crowded each pair. */}
    <Space
      direction="vertical"
      style={{ marginBottom: vars.whitespace4 }}
    >
      <h3 style={{ color: "#29256E" }}>{label}</h3>
      <div style={{ color: "black", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value || "-"}</div>
    </Space>
  </Col>
);

/** The pencil in a card's header — the SPA's own edit affordance. */
const EditAction = ({ onEdit }: { onEdit: () => void }) => (
  <Icon
    icon="carbon:edit"
    role="button"
    aria-label="Edit"
    style={{ color: vars.strawberry, cursor: "pointer", fontSize: 20, verticalAlign: "middle" }}
    onClick={onEdit}
  />
);

/** Each card in the stack, spaced the way the SPA review spaces them. Also
    the work order detail page's sections, so the two read alike. */
export const Section = ({
  title,
  onEdit,
  children,
}: {
  title: string;
  /** No pencil when there is nothing to edit. */
  onEdit?: () => void;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: 24 }}>
    <WrapperCard
      title={title}
      smallHeader
      action={onEdit ? <EditAction onEdit={onEdit} /> : undefined}
    >
      {children}
    </WrapperCard>
  </div>
);

/**
 * Step 5 — Review & Submit.
 *
 * A read-back of everything the wizard collected. Each card's pencil jumps to
 * the step that owns those fields, so a correction never means walking back
 * through the whole flow.
 */
/**
 * A picked file, as a URL the viewer can open.
 *
 * The step holds `File` objects rather than uploaded URLs, so one is minted on
 * demand and released when the preview closes — an object URL the browser
 * keeps alive until it is revoked.
 */
const useFilePreview = () => {
  const [preview, setPreview] = useState<{ name: string; url: string; revoke?: boolean } | null>(null);

  useEffect(
    () => () => {
      if (preview?.revoke) URL.revokeObjectURL(preview.url);
    },
    [preview]
  );

  const open = (item: File | { name: string; url: string }) => {
    if ("url" in item) {
      setPreview({ name: item.name, url: item.url, revoke: false });
    } else {
      setPreview({ name: item.name, url: URL.createObjectURL(item), revoke: true });
    }
  };
  const close = () => {
    if (preview?.revoke) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return { preview, open, close };
};

export const ReviewSubmit = ({
  values,
  templates,
  items,
  files,
  savedDocuments = [],
  links,
  reviewers,
  approvers,
  authority,
  onBack,
  onSubmit,
  onEditStep,
  isSubmitting,
  onSaveDraft,
  isSavingDraft,
}: Props) => {
  const { preview, open: openPreview, close: closePreview } = useFilePreview();
  const sorColumns = sorLineColumns;

  const reviewerColumns: TableColumnsType<any> = [
    { title: "Name", dataIndex: "name" },
    { title: "Email Address", dataIndex: "email" },
    { title: "Approving Order", dataIndex: "order" },
  ];

  const approverColumns: TableColumnsType<any> = [
    { title: "Designation", dataIndex: "designation" },
    { title: "Name", dataIndex: "name" },
    { title: "Email Address", dataIndex: "email" },
    { title: "Approving Order", dataIndex: "order" },
  ];

  const total = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const authorityLabel =
    approvalAuthorities.find((entry) => entry.key === authority)?.label ?? "-";

  /* Approving order as Submit sends it: every reviewer is 1, the approvers one
     step per designation (all R1 2, R2 3, …) — what the detail page reads back. */
  const reviewerRows = reviewers
    .filter((r) => r.name || r.email)
    .map((r) => ({ ...r, order: REVIEWER_ORDER }));
  const approverRows = approvers.map((a, index, list) => ({
    ...a,
    order: approverOrder(list, index),
  }));
  const populatedLinks = links.filter((l) => l.documentName || l.documentLink);


  return (
    <>
      <Section
        title="Project Information"
        onEdit={() => onEditStep(0)}
      >
        <Row gutter={24}>
          <Field label="Requester Name" value={values.requesterName} />
          <Field label="Requester Email" value={values.requesterEmail} />
          <Field label="Date Request" value={asDate(values.dateRequest)} />
          <Field label="Job Title" value={values.jobTitle} />
          <Field label="Subsidiary/Division" value={values.subsidiary} />
          <Field label="Cost Centre" value={values.costCentre} />
          <Field label="Department" value={values.department} />
          <Field label="PR No." value={values.prNo} />
          <Field label="Budget Status" value={values.budgetStatus} />
          <Field label="Background" value={values.background} full />
          <Field label="Scope of Work" value={values.scopeOfWork} full />
          <Field label="Commencement Date" value={asDate(values.commencementDate)} />
          <Field label="Completion Date" value={asDate(values.completionDate)} />
          {/* The *Name companions, not the raw ids the selects store. */}
          <Field label="Region" value={values.regionName || values.region} />
          <Field
            label="Work Order Contract Category"
            value={values.contractCategoryName || values.contractCategory}
          />
          <Field label="Sub Region" value={values.subRegionName || values.subRegion} />
          <Field label="Procurement Approach" value={approachLabel(values.procurementApproach)} />
          <Field label="Vendor" value={values.vendorName || values.vendor} />
        </Row>
      </Section>

      <Section
        title="SOR Details"
        onEdit={() => onEditStep(1)}
      >
        <Table<SorItem>
          columns={sorColumns}
          dataSource={items}
          pagination={false}
          rowKey="key"
          tableLayout="fixed"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell
                index={0}
                colSpan={6}
              >
                <Typography.Text
                  strong
                  style={{ display: "block", textAlign: "right" }}
                >
                  Total Amount
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                <Typography.Text strong>{total.toLocaleString()}</Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Section>

      <Section
        title="Project Documents"
        onEdit={() => onEditStep(2)}
      >
        {files.length === 0 && (!savedDocuments || savedDocuments.length === 0) ? (
          <Typography.Text type="secondary">No documents attached</Typography.Text>
        ) : (
          <>
            {savedDocuments?.map((doc: any, index: number) => (
              <div
                className={styles.fileRow}
                key={doc.key ?? `saved-${index}`}
              >
                <Icon
                  icon="vscode-icons:file-type-pdf2"
                  className={styles.fileIcon}
                />
                <Typography.Text>{doc.name}</Typography.Text>
                <Icon
                  icon="mdi:eye-outline"
                  className={styles.viewIcon}
                  onClick={() => openPreview({ name: doc.name, url: doc.url })}
                />
              </div>
            ))}
            {files.map((file, index) => (
              <div
                className={styles.fileRow}
                key={`${file.name}-${index}`}
              >
                <Icon
                  icon="vscode-icons:file-type-pdf2"
                  className={styles.fileIcon}
                />
                <Typography.Text>{file.name}</Typography.Text>
                {/* Opens this document alone, leaving the others untouched. */}
                <Icon
                  icon="mdi:eye-outline"
                  className={styles.viewIcon}
                  onClick={() => openPreview(file)}
                />
              </div>
            ))}
          </>
        )}

        <h3 style={{ color: "#29256E", margin: "24px 0 12px" }}>Additional Document Details</h3>
        {populatedLinks.length === 0 ? (
          <Row gutter={24}>
            <Field label="Document Name" value="-" />
            <Field label="Document Link" value="-" />
          </Row>
        ) : (
          populatedLinks.map((link) => (
            <Row
              gutter={24}
              key={link.key}
            >
              <Field label="Document Name" value={link.documentName} />
              <Field
                label="Document Link"
                value={
                  hrefOf(link.documentLink) && (
                    <a
                      href={hrefOf(link.documentLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: "break-all" }}
                    >
                      {link.documentLink}
                    </a>
                  )
                }
              />
            </Row>
          ))
        )}
      </Section>

      <PDFViewer
        show={!!preview}
        setShow={closePreview}
        title={preview?.name ?? ""}
        url={preview?.url ?? ""}
      />

      <Section
        title="Reviewers"
        onEdit={() => onEditStep(3)}
      >
        <Table
          columns={reviewerColumns}
          dataSource={reviewerRows}
          pagination={false}
          rowKey="key"
        />
      </Section>

      <Section
        title="Approvers"
        onEdit={() => onEditStep(3)}
      >
        <Row gutter={24}>
          <Field label="Approval Authority" value={authorityLabel} />
        </Row>
        <Table
          columns={approverColumns}
          dataSource={approverRows}
          pagination={false}
          rowKey="key"
        />
      </Section>

      <Row
        justify="end"
        gutter={16}
        className={styles.stepActions}
      >
        <Col>
          <Button onClick={onBack}>Back</Button>
        </Col>
        <Col>
          <Button
            loading={isSavingDraft}
            onClick={onSaveDraft}
          >
            Save as draft
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Submit
          </Button>
        </Col>
      </Row>
    </>
  );
};
